import type { ReleaseNote } from './types';

type Version = [major: number, minor: number, patch: number];

const parseVersion = (version: string): Version | null => {
  const match = /^v?(\d+)\.(\d+)\.(\d+)$/.exec(version.trim());
  if (!match) return null;

  return [Number(match[1]), Number(match[2]), Number(match[3])];
};

const compare = (a: Version, b: Version) => a[0] - b[0] || a[1] - b[1] || a[2] - b[2];

/** Negative when `a` is older than `b`; `null` when either isn't `x.y.z`. */
export const compareVersions = (a: string, b: string): number | null => {
  const parsedA = parseVersion(a);
  const parsedB = parseVersion(b);
  if (!parsedA || !parsedB) return null;

  return compare(parsedA, parsedB);
};

/** Patch versions never get a modal, even if a note for one slips into the file. */
export const selectUnseenNotes = (
  notes: ReleaseNote[],
  lastSeen: string,
  appVersion: string
): ReleaseNote[] => {
  const seen = parseVersion(lastSeen);
  const current = parseVersion(appVersion);
  if (!seen || !current) return [];

  return notes
    .map((note) => ({ note, version: parseVersion(note.version) }))
    .filter(
      (entry): entry is { note: ReleaseNote; version: Version } =>
        entry.version !== null &&
        entry.version[2] === 0 &&
        compare(entry.version, seen) > 0 &&
        compare(entry.version, current) <= 0
    )
    .sort((a, b) => compare(b.version, a.version))
    .map(({ note }) => note);
};
